import { Button } from '@crowdex/ui/components/button';
import { MUTED, PAGE_TITLE } from '@crowdex/ui/lib/desktop-styles';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { authorizeCloudLink } from '@/lib/cloud-link';
import { errorMessage } from '@/lib/error-message';
import { navigateApp } from '@/vite/navigation-store';

/**
 * On Crowdex Cloud: the page the Crowdex app of the user's computer opens to link itself
 * to their account. Approving sends the browser back to that app with a one-time code.
 */
export function CloudLinkConsent({
  email,
  parameters,
}: {
  email: string;
  parameters: URLSearchParams;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const challenge = parameters.get('challenge');
  const callback = parameters.get('callback');
  const state = parameters.get('state');
  const valid = challenge !== null && callback !== null && state !== null;

  async function approve() {
    if (!valid) return;
    setPending(true);
    setError(null);
    try {
      window.location.assign(await authorizeCloudLink({ challenge, callback, state }));
    } catch (cause) {
      setError(errorMessage(cause));
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        <header className="space-y-1">
          <h1 className={PAGE_TITLE}>Link the Crowdex app</h1>
          <p className={MUTED}>
            {valid
              ? `The Crowdex app on this computer asks to use your cloud account (${email}): to send conversations to Crowdex Cloud, see the ones running there and use your connections.`
              : 'This link is incomplete. Start again from Settings → Account in the Crowdex app.'}
          </p>
        </header>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button onClick={() => navigateApp('/desktop', true)} type="button" variant="ghost">
            Cancel
          </Button>
          <Button
            className="gap-2"
            disabled={!valid || pending}
            onClick={() => void approve()}
            type="button"
          >
            {pending && <LoaderCircle className="size-3.5 animate-spin" />}
            Allow
          </Button>
        </div>
      </div>
    </main>
  );
}
